/**
 * Детальная карточка одного этапа (приёма) таймлайна.
 *
 * Левая колонка — что входит в этап (чеклист услуг), правая — информация
 * о приёме: плановая/фактическая дата, давление, комментарий, стоимость.
 *
 * Чеклист интерактивный: услуги можно отметить галочками, затем нажать
 * «Выполнено» — откроется окно комментария, после сохранения отметки и
 * комментарий уходят на сервер через onCompleteStage.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import {
    IoCalendarClearOutline,
    IoCalendarOutline,
    IoCheckmarkOutline,
    IoDocumentTextOutline,
    IoHeartOutline,
    IoListOutline,
} from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../../i18n/config";
import { Button } from "../../../components/ui";
import { formatDayMonth, formatRange } from "./planTimelineData";

const STATE_LABELS = {
    done: "plan.timeline.stateDone",
    current: "plan.timeline.stateCurrent",
    future: "plan.timeline.stateFuture",
};

function PatientPlanTimelineStage({ stage, planId, onCompleteStage, busy = false }) {
    const { t, i18n } = useTranslation("patient");
    const locale = getLocale(i18n.language);

    // Отметки в чеклисте — локальные до нажатия «Выполнено».
    const [checked, setChecked] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);

    const services = stage?.services || [];

    // Набор «было отмечено на сервере» — чтобы отправлять только изменения.
    const initialChecked = useMemo(
        () =>
            Object.fromEntries(
                services.map((service) => [service.key, Boolean(service.done)]),
            ),
        [services],
    );

    useEffect(() => {
        setChecked(initialChecked);
        setShowModal(false);
        setComment("");
    }, [initialChecked]);

    const toggled = services.some(
        (service) => Boolean(checked[service.key]) !== Boolean(initialChecked[service.key]),
    );
    const chosenCount = services.filter((service) => checked[service.key]).length;

    if (!stage) return null;

    const percent =
        stage.total > 0 ? Math.round((stage.done / stage.total) * 100) : 0;
    const range = formatRange(stage.start, stage.end);
    const stateLabel = t(STATE_LABELS[stage.state] || STATE_LABELS.future);

    return (
        <div className={`plan-timeline-stage is-${stage.state}`}>
            {/* Шапка этапа: номер-иконка, название, статус, даты. */}
            <div className="plan-timeline-stage__head">
                <div className="plan-timeline-stage__index">
                    {stage.state === "done" ? (
                        <IoCheckmarkOutline aria-hidden="true" size={24} />
                    ) : (
                        stage.number
                    )}
                </div>

                <div className="plan-timeline-stage__meta">
                    <div className="plan-timeline-stage__titles">
                        <h2 className="plan-timeline-stage__title">
                            {stage.title
                                ? t("plan.timeline.stageTitle", {
                                      number: stage.number,
                                      title: stage.title,
                                  })
                                : t("plan.timeline.stageOnly", {
                                      number: stage.number,
                                  })}
                        </h2>
                        <span
                            className={`plan-timeline-stage__badge is-${stage.state}`}>
                            <span className="plan-timeline-stage__dot" />
                            {stateLabel}
                        </span>
                    </div>

                    <div className="plan-timeline-stage__facts">
                        {range ? (
                            <span className="plan-timeline-stage__fact">
                                <IoCalendarOutline
                                    aria-hidden="true"
                                    size={14}
                                />
                                {range}
                            </span>
                        ) : null}
                        <span className="plan-timeline-stage__fact">
                            <IoListOutline aria-hidden="true" size={14} />
                            {t("plan.timeline.completedOf", {
                                done: stage.done,
                                total: stage.total,
                            })}
                        </span>
                        {planId ? (
                            <span className="plan-timeline-stage__fact">
                                <IoDocumentTextOutline
                                    aria-hidden="true"
                                    size={14}
                                />
                                {t("plan.matrix.planNumber", { id: planId })}
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Полоса прогресса этапа. */}
            <div className="plan-timeline-stage__progress">
                <div className="plan-timeline-stage__progress-head">
                    <span>{t("plan.timeline.progress")}</span>
                    <span>{percent}%</span>
                </div>
                <div
                    className="plan-timeline-stage__bar"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={percent}>
                    <span
                        className="plan-timeline-stage__bar-fill"
                        style={{ width: `${percent}%` }}
                    />
                </div>
            </div>

            {/* Две колонки: услуги этапа и информация о приёме. */}
            <div className="plan-timeline-stage__grid">
                <div className="plan-timeline-stage__col">
                    <div className="plan-timeline-stage__block-title">
                        <IoListOutline aria-hidden="true" size={17} />
                        {t("plan.timeline.includes")}
                    </div>

                    {stage.services.length === 0 ? (
                        <div className="plan-timeline-stage__empty">
                            {t("plan.timeline.noServices")}
                        </div>
                    ) : (
                        <ul className="plan-timeline-checks">
                            {services.map((service) => {
                                const isChecked = Boolean(checked[service.key]);
                                return (
                                    <li
                                        key={service.key}
                                        className={`plan-timeline-check${
                                            isChecked ? " is-done" : ""
                                        }${onCompleteStage ? " is-editable" : ""}`}>
                                        <button
                                            type="button"
                                            className="plan-timeline-check__box"
                                            role="checkbox"
                                            aria-checked={isChecked}
                                            aria-label={service.name}
                                            disabled={!onCompleteStage || busy}
                                            onClick={() =>
                                                setChecked((prev) => ({
                                                    ...prev,
                                                    [service.key]: !isChecked,
                                                }))
                                            }>
                                            {isChecked ? (
                                                <IoCheckmarkOutline
                                                    aria-hidden="true"
                                                    size={12}
                                                />
                                            ) : null}
                                        </button>
                                        <span className="plan-timeline-check__body">
                                            <span className="plan-timeline-check__name">
                                                {service.name}
                                            </span>
                                            {service.composition ? (
                                                <span className="plan-timeline-check__comp">
                                                    {service.composition}
                                                </span>
                                            ) : null}
                                        </span>
                                        <span className="plan-timeline-check__side">
                                            {service.date ? (
                                                <span className="plan-timeline-check__date">
                                                    {formatDayMonth(service.date)}
                                                </span>
                                            ) : null}
                                            <span className="plan-timeline-check__price">
                                                {service.price.toLocaleString(
                                                    locale,
                                                )}{" "}
                                                ₽
                                            </span>
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {/* Сохранение отметок — сразу под списком услуг. */}
                    {onCompleteStage ? (
                        <div className="plan-timeline-checks__foot">
                            <span className="plan-timeline-checks__foot-hint">
                                {services.length === 0
                                    ? t("plan.timeline.noServicesToMark")
                                    : t("plan.timeline.markedOf", {
                                          done: chosenCount,
                                          total: services.length,
                                      })}
                            </span>
                            <Button
                                disabled={
                                    busy ||
                                    saving ||
                                    services.length === 0 ||
                                    (!toggled && !stage.comment)
                                }
                                icon="check"
                                loading={saving}
                                onClick={() => {
                                    setComment(stage.comment || "");
                                    setShowModal(true);
                                }}
                                variant="primary">
                                {t("plan.timeline.markDoneSave")}
                            </Button>
                        </div>
                    ) : null}
                </div>

                <div className="plan-timeline-stage__col">
                    <div className="plan-timeline-stage__block-title">
                        <IoCalendarClearOutline aria-hidden="true" size={17} />
                        {t("plan.timeline.details")}
                    </div>

                    <dl className="plan-timeline-facts">
                        <div className="plan-timeline-facts__row">
                            <dt>{t("plan.timeline.planDate")}</dt>
                            <dd>
                                {formatDayMonth(stage.planDate) ||
                                    t("plan.timeline.notSet")}
                            </dd>
                        </div>
                        <div className="plan-timeline-facts__row">
                            <dt>{t("plan.timeline.actualDate")}</dt>
                            <dd>
                                {formatDayMonth(stage.actualDate) ||
                                    t("plan.timeline.notSet")}
                            </dd>
                        </div>
                        <div className="plan-timeline-facts__row">
                            <dt>
                                <IoHeartOutline aria-hidden="true" size={13} />
                                {t("plan.timeline.pressure")}
                            </dt>
                            <dd>
                                {stage.pressure || t("plan.timeline.notSet")}
                            </dd>
                        </div>
                        <div className="plan-timeline-facts__row">
                            <dt>{t("plan.timeline.servicesTotal")}</dt>
                            <dd>
                                {stage.sum.toLocaleString(locale)} ₽
                            </dd>
                        </div>
                    </dl>

                    <div className="plan-timeline-stage__comment">
                        <div className="plan-timeline-stage__comment-label">
                            {t("plan.timeline.comment")}
                        </div>
                        <div className="plan-timeline-stage__comment-text">
                            {stage.comment || t("plan.timeline.noComment")}
                        </div>
                    </div>
                </div>
            </div>

            {/* Окно комментария к выполненному этапу. */}
            <Modal
                centered
                className="plan-timeline-done-modal plan-matrix-modal"
                onHide={() => {
                    if (!saving) setShowModal(false);
                }}
                show={showModal}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t("plan.timeline.markDoneTitle", {
                            number: stage.number,
                        })}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="plan-timeline-done-modal__hint">
                        {t("plan.timeline.markDoneHint", {
                            count: chosenCount,
                            total: services.length,
                        })}
                    </p>
                    <label
                        className="plan-timeline-done-modal__label"
                        htmlFor="plan-timeline-comment">
                        {t("plan.timeline.commentLabel")}
                    </label>
                    <textarea
                        className="form-control plan-timeline-done-modal__input"
                        id="plan-timeline-comment"
                        onChange={(event) => setComment(event.target.value)}
                        placeholder={t("plan.timeline.commentPlaceholder")}
                        rows={4}
                        value={comment}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        disabled={saving}
                        onClick={() => setShowModal(false)}
                        variant="secondary">
                        {t("plan.timeline.cancel")}
                    </Button>
                    <Button
                        loading={saving}
                        onClick={async () => {
                            if (!onCompleteStage) return;
                            setSaving(true);
                            try {
                                await onCompleteStage({
                                    stage,
                                    services,
                                    checked,
                                    initialChecked,
                                    comment: String(comment || "").trim(),
                                });
                                setShowModal(false);
                            } finally {
                                setSaving(false);
                            }
                        }}
                        variant="primary">
                        {t("plan.timeline.markDoneSave")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default PatientPlanTimelineStage;
